// ── DEMO MODE mock layer (temporary, build-time) ────────────────────────────
// Serves rich, all-Finnish fixture data for every citizen-portal feature domain
// that was swapped to real `apiClient` calls, so the PJH demo on
// renyy.wastehero.io (the `staging` branch) shows every section fully populated
// with NO empty/error states — even though the staging tenant has no data.
//
// Gated entirely on isDemoMode() (REACT_APP_DEMO_MODE=true, set only as the
// `staging` branch-scoped Vercel variable — see docs/deploy/vercel-env.md).
// Modelled on src/api/devMock.ts: a per-request adapter
// override intercepts the feature GET/writes and returns fixtures, while the
// real address-search login endpoints (property list/detail) still hit the
// backend. A response interceptor additionally strips `rights` from the real
// property detail so capability-gated actions (order services, send message,
// manage pool…) fail open for the demo.
//
// Revert = remove installDemoMocks() from client.tsx and delete this file. The
// still-mock domains (principals) guard their own demo branch on isDemoMode().
import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { isDemoMode } from 'src/api/state';
import { reportsDevMatchers } from 'src/api/reports/dev-mock';
import type {
  ConversationDetail,
  ConversationSummary,
  Message,
} from 'src/api/messages';
import type { Pool, PoolSummary, PartnerPool } from 'src/api/pools';
import type { ChannelPrefs } from 'src/api/comms/channels';
import type { InfoBanner } from 'src/api/comms/banners';
import type { FastTicket, TicketMessage } from 'src/api/tickets';

type MockResult = { status: number; data: unknown };
type Matcher = (config: InternalAxiosRequestConfig) => MockResult | null;

const path = (config: InternalAxiosRequestConfig) =>
  (config.url || '').replace(/^\/+/, '');
const method = (config: InternalAxiosRequestConfig) =>
  config.method?.toUpperCase();

// ── Messages fixtures (REQ-21) ───────────────────────────────────────────────
const MSG_CONVERSATIONS: ConversationSummary[] = [
  {
    id: 'conv-1',
    subject: 'Poikkeava tyhjennysaika viikolla 28',
    lastMessagePreview:
      'Kiitos ilmoituksesta — siirsimme tyhjennyksen keskiviikolle.',
    lastMessageAt: '2026-07-22T08:15:00Z',
    unread: true,
  },
  {
    id: 'conv-2',
    subject: 'Biojäteastian vaihto',
    lastMessagePreview:
      'Uusi 240 litran biojäteastia toimitetaan ensi viikolla.',
    lastMessageAt: '2026-07-18T13:40:00Z',
    unread: false,
    ticketId: 'TIK-10482',
  },
  {
    id: 'conv-3',
    subject: 'Laskun eräpäivän siirto',
    lastMessagePreview: 'Eräpäivää on siirretty 31.7.2026 asti.',
    lastMessageAt: '2026-07-10T09:05:00Z',
    unread: false,
  },
];

const msg = (
  id: string,
  author: Message['author'],
  authorName: string,
  body: string,
  createdAt: string
): Message => ({ id, author, authorName, body, attachments: [], createdAt });

const MSG_DETAILS: Record<string, ConversationDetail> = {
  'conv-1': {
    id: 'conv-1',
    subject: 'Poikkeava tyhjennysaika viikolla 28',
    messages: [
      msg(
        'm1',
        'customer',
        'Anna Virtanen',
        'Hei, huomasin että sekajäteastiaa ei tyhjennetty maanantaina. Onko aikataulu muuttunut?',
        '2026-07-21T15:20:00Z'
      ),
      msg(
        'm2',
        'company',
        'PJH asiakaspalvelu',
        'Hei Anna! Arkipyhän vuoksi tyhjennys siirtyi tällä viikolla keskiviikolle. Astia tyhjennetään huomenna aamupäivällä.',
        '2026-07-22T08:15:00Z'
      ),
    ],
  },
  'conv-2': {
    id: 'conv-2',
    subject: 'Biojäteastian vaihto',
    ticketId: 'TIK-10482',
    messages: [
      msg(
        'm1',
        'customer',
        'Anna Virtanen',
        'Haluaisin vaihtaa biojäteastian isompaan. Nykyinen 140 l on liian pieni.',
        '2026-07-17T10:00:00Z'
      ),
      msg(
        'm2',
        'company',
        'PJH asiakaspalvelu',
        'Selvä! Toimitamme 240 litran biojäteastian ensi viikolla. Vanha astia noudetaan samalla käynnillä.',
        '2026-07-18T13:40:00Z'
      ),
    ],
  },
  'conv-3': {
    id: 'conv-3',
    subject: 'Laskun eräpäivän siirto',
    messages: [
      msg(
        'm1',
        'customer',
        'Anna Virtanen',
        'Voisiko laskun 2026-04471 eräpäivää siirtää hieman?',
        '2026-07-09T12:30:00Z'
      ),
      msg(
        'm2',
        'company',
        'PJH laskutus',
        'Totta kai — siirsimme eräpäivän 31.7.2026 asti. Uusi lasku näkyy tililläsi hetken kuluttua.',
        '2026-07-10T09:05:00Z'
      ),
    ],
  },
};

const newConversation = (
  subject: string,
  body: string
): ConversationDetail => ({
  id: `conv-new-${subject.length}`,
  subject: subject || 'Uusi viesti',
  messages: [
    msg('mnew', 'customer', 'Anna Virtanen', body, '2026-07-24T09:00:00Z'),
  ],
});

const messagesMatchers: Matcher[] = [
  // unread-count — before the conversation-detail matcher.
  (config) => {
    if (method(config) !== 'GET') return null;
    if (
      !/^property\/[^/?]+\/conversations\/unread-count(?:\?|$)/.test(
        path(config)
      )
    )
      return null;
    return {
      status: 200,
      data: { count: MSG_CONVERSATIONS.filter((c) => c.unread).length },
    };
  },
  // conversation detail: GET property/:id/conversations/:cid
  (config) => {
    if (method(config) !== 'GET') return null;
    const m = /^property\/[^/?]+\/conversations\/([^/?]+)(?:\?|$)/.exec(
      path(config)
    );
    if (!m) return null;
    return { status: 200, data: MSG_DETAILS[m[1]] ?? MSG_DETAILS['conv-1'] };
  },
  // conversations list: GET property/:id/conversations
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^property\/[^/?]+\/conversations(?:\?|$)/.test(path(config)))
      return null;
    return { status: 200, data: MSG_CONVERSATIONS };
  },
  // mark read: POST .../conversations/:cid/read
  (config) => {
    if (method(config) !== 'POST') return null;
    if (
      !/^property\/[^/?]+\/conversations\/[^/?]+\/read(?:\?|$)/.test(
        path(config)
      )
    )
      return null;
    return { status: 204, data: undefined };
  },
  // send message: POST .../conversations/:cid/messages
  (config) => {
    if (method(config) !== 'POST') return null;
    if (
      !/^property\/[^/?]+\/conversations\/[^/?]+\/messages(?:\?|$)/.test(
        path(config)
      )
    )
      return null;
    const body = ((config.data ?? {}) as { body?: string }).body ?? '';
    return {
      status: 201,
      data: msg(
        'm-sent',
        'customer',
        'Anna Virtanen',
        body,
        '2026-07-24T09:10:00Z'
      ),
    };
  },
  // start conversation: POST property/:id/conversations
  (config) => {
    if (method(config) !== 'POST') return null;
    if (!/^property\/[^/?]+\/conversations(?:\?|$)/.test(path(config)))
      return null;
    const body = (config.data ?? {}) as { subject?: string; body?: string };
    return {
      status: 201,
      data: newConversation(body.subject ?? '', body.body ?? ''),
    };
  },
];

// ── Pools / kimppa fixtures (REQ-24/25) ──────────────────────────────────────
const POOL: Pool = {
  id: 'pool-1',
  name: 'Kauppakadun kimppa-astia',
  viewerRole: 'contact_person',
  viewerMemberId: 'pm-1',
  contactPerson: {
    name: 'Anna Virtanen',
    phone: '+358 40 123 4567',
    email: 'anna.virtanen@example.fi',
  },
  deputy: { name: 'Mikko Laine', email: 'mikko.laine@example.fi' },
  service: {
    emptyingAddress: 'Kauppakatu 12, 15140 Lahti',
    bins: [
      {
        fractionId: 'mixed',
        fractionName: 'Sekajäte',
        sizeLiters: 660,
        count: 1,
      },
      { fractionId: 'bio', fractionName: 'Biojäte', sizeLiters: 240, count: 1 },
      {
        fractionId: 'card',
        fractionName: 'Kartonki',
        sizeLiters: 660,
        count: 1,
      },
    ],
    schedule: [
      {
        fractionId: 'mixed',
        label: 'Sekajäte',
        nextPickup: '2026-07-29',
        intervalWeeks: 2,
      },
      {
        fractionId: 'bio',
        label: 'Biojäte',
        nextPickup: '2026-07-25',
        intervalWeeks: 1,
      },
      {
        fractionId: 'card',
        label: 'Kartonki',
        nextPickup: '2026-08-05',
        intervalWeeks: 4,
      },
    ],
  },
  shareLimits: { minPct: 5, maxPct: 80 },
  members: [
    {
      id: 'pm-1',
      propertyId: 'prop-a',
      addressLabel: 'Kauppakatu 12 A',
      contactName: 'Anna Virtanen',
      sharePct: 40,
      status: 'active',
      isDeputy: false,
    },
    {
      id: 'pm-2',
      propertyId: 'prop-b',
      addressLabel: 'Kauppakatu 12 B',
      contactName: 'Mikko Laine',
      sharePct: 35,
      status: 'active',
      isDeputy: true,
    },
    {
      id: 'pm-3',
      propertyId: 'prop-c',
      addressLabel: 'Kauppakatu 14',
      contactName: 'Sari Nieminen',
      sharePct: 25,
      status: 'active',
      isDeputy: false,
    },
  ],
};

const POOL_SUMMARIES: PoolSummary[] = [
  {
    id: 'pool-1',
    name: 'Kauppakadun kimppa-astia',
    viewerRole: 'contact_person',
    memberCount: 3,
    mySharePct: 40,
  },
];

const PARTNER_POOL: PartnerPool = {
  id: 'pool-1',
  name: 'Kauppakadun kimppa-astia',
  viewerRole: 'partner',
  contactPerson: POOL.contactPerson,
  service: POOL.service,
  myShare: { memberId: 'pm-3', sharePct: 25, status: 'active' },
};

const poolsMatchers: Matcher[] = [
  // partner withdrawal status: GET pool/:id/me/withdrawal (null = no request)
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^pool\/[^/?]+\/me\/withdrawal(?:\?|$)/.test(path(config)))
      return null;
    return { status: 200, data: null };
  },
  // partner-scoped pool: GET pool/:id/me
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^pool\/[^/?]+\/me(?:\?|$)/.test(path(config))) return null;
    return { status: 200, data: PARTNER_POOL };
  },
  // pool detail: GET pool/:id  (writes echo the pool back so the editor settles)
  (config) => {
    const m = /^pool\/([^/?]+)(?:\?|$)/.exec(path(config));
    if (!m) return null;
    if (method(config) === 'GET' || method(config) === 'PATCH')
      return { status: 200, data: POOL };
    return null;
  },
  // pool service/shares/deputy/members writes → echo pool
  (config) => {
    if (!/^pool\/[^/?]+\/(service|shares|deputy|members)/.test(path(config)))
      return null;
    if (method(config) === 'DELETE') return { status: 200, data: POOL };
    return { status: 200, data: POOL };
  },
  // pools for property: GET property/:id/pools
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^property\/[^/?]+\/pools(?:\?|$)/.test(path(config))) return null;
    return { status: 200, data: { pools: POOL_SUMMARIES } };
  },
];

// ── Cases / Sager fixtures (tickets) ─────────────────────────────────────────
// Cases render in the Beskeder inbox alongside conversations. The inbox filters
// cases to the property's portal-enabled ticket types (portalTickets.ts), except
// closed cases (status 6/7) which always stay visible. So the closed fixtures
// below always render; the one active (In Progress, status 5) fixture uses the
// real property's own first ticket-type name (captured from the intercepted
// property detail) so it survives the portal-type filter too.
let capturedTicketType: string | null = null;

const tmsg = (
  id: string,
  author: TicketMessage['author'],
  body: string,
  created_at: string
): TicketMessage => ({ id, author, body, created_at });

const demoTickets = (): FastTicket[] => [
  {
    id: 'tk-1',
    ticket_id: 'TIK-10482',
    created_at: '2026-07-17T10:00:00Z',
    ticket_type: capturedTicketType ?? 'Biojäteastian vaihto',
    status: 5, // In Progress
    description: 'Biojäteastian vaihto suurempaan (140 l → 240 l).',
    preferred_date: '2026-07-28',
    messages: [
      tmsg(
        'tm1',
        'customer',
        'Haluaisin vaihtaa biojäteastian isompaan.',
        '2026-07-17T10:00:00Z'
      ),
      tmsg(
        'tm2',
        'company',
        'Kiitos pyynnöstä — käsittelemme astianvaihdon tällä viikolla.',
        '2026-07-18T08:30:00Z'
      ),
    ],
  },
  {
    id: 'tk-2',
    ticket_id: 'TIK-10310',
    created_at: '2026-06-20T09:00:00Z',
    ticket_type: 'Suurikokoisen jätteen nouto',
    status: 6, // Completed
    description: 'Suurikokoisen jätteen nouto (sohva ja nojatuoli).',
    resolved_at: '2026-06-27T11:00:00Z',
    messages: [
      tmsg(
        'tm1',
        'customer',
        'Pyytäisin noutoa vanhalle sohvalle.',
        '2026-06-20T09:00:00Z'
      ),
      tmsg(
        'tm2',
        'company',
        'Nouto on tehty. Kiitos asioinnista!',
        '2026-06-27T11:00:00Z'
      ),
    ],
  },
  {
    id: 'tk-3',
    ticket_id: 'TIK-10188',
    created_at: '2026-05-30T14:00:00Z',
    ticket_type: 'Lisätyhjennys',
    status: 7, // Rejected
    description: 'Lisätyhjennyspyyntö sekajäteastialle.',
    resolved_at: '2026-06-02T09:00:00Z',
    messages: [
      tmsg(
        'tm1',
        'customer',
        'Tarvitsisin lisätyhjennyksen ensi viikolle.',
        '2026-05-30T14:00:00Z'
      ),
      tmsg(
        'tm2',
        'company',
        'Valitettavasti lisätyhjennystä ei voitu toteuttaa pyydetyllä aikataululla.',
        '2026-06-02T09:00:00Z'
      ),
    ],
  },
];

const ticketsMatchers: Matcher[] = [
  // reply to a case: POST property/:id/tickets/:tid/messages → echo the reply
  (config) => {
    if (method(config) !== 'POST') return null;
    if (
      !/^property\/[^/?]+\/tickets\/[^/?]+\/messages(?:\?|$)/.test(path(config))
    )
      return null;
    const body = ((config.data ?? {}) as { body?: string }).body ?? '';
    return {
      status: 201,
      data: tmsg('tm-reply', 'customer', body, '2026-07-24T09:15:00Z'),
    };
  },
  // case detail: GET property/:id/tickets/:tid
  (config) => {
    if (method(config) !== 'GET') return null;
    const m = /^property\/[^/?]+\/tickets\/([^/?]+)(?:\?|$)/.exec(path(config));
    if (!m) return null;
    const ticket = demoTickets().find(
      (t) => t.id === m[1] || t.ticket_id === m[1]
    );
    return { status: 200, data: ticket ?? demoTickets()[0] };
  },
  // cases list: GET property/:id/tickets
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^property\/[^/?]+\/tickets(?:\?|$)/.test(path(config))) return null;
    return { status: 200, data: demoTickets() };
  },
];

// ── Communication preferences fixtures (REQ-8/10) ────────────────────────────
const CHANNEL_PREFS: ChannelPrefs = {
  channels: [
    {
      id: 'email',
      enabled: true,
      address: 'anna.virtanen@example.fi',
      available: true,
    },
    { id: 'sms', enabled: true, address: '+358 40 123 4567', available: true },
    { id: 'letter', enabled: false, address: null, available: true },
    { id: 'suomifi', enabled: false, address: null, available: false },
  ],
  restrictToSingle: false,
  primaryChannel: 'email',
};

// ── Info banners (REQ-22) ────────────────────────────────────────────────────
const BANNERS: InfoBanner[] = [
  {
    id: 'banner-1',
    severity: 'info',
    title: 'Kesäajan keräysaikataulu',
    body: 'Sekajätteen tyhjennykset ovat heinäkuussa viikon välein. Tarkista astiakohtaiset ajat Raportit-osiosta.',
    dismissible: true,
    startsAt: '2026-07-01T00:00:00Z',
    endsAt: '2026-08-31T23:59:00Z',
  },
];

const commsMatchers: Matcher[] = [
  // GET/PATCH comms/channels  (PATCH echoes the requested toggles back)
  (config) => {
    if (!/^comms\/channels(?:\?|$)/.test(path(config))) return null;
    if (method(config) === 'GET') return { status: 200, data: CHANNEL_PREFS };
    if (method(config) === 'PATCH') {
      const body = (config.data ?? {}) as {
        channels?: { id: string; enabled: boolean }[];
        primaryChannel?: ChannelPrefs['primaryChannel'];
      };
      const wanted = new Map(
        (body.channels ?? []).map((c) => [c.id, c.enabled])
      );
      return {
        status: 200,
        data: {
          ...CHANNEL_PREFS,
          channels: CHANNEL_PREFS.channels.map((c) =>
            wanted.has(c.id) ? { ...c, enabled: !!wanted.get(c.id) } : c
          ),
          primaryChannel: body.primaryChannel ?? CHANNEL_PREFS.primaryChannel,
        },
      };
    }
    return null;
  },
  // suomi.fi eligibility (strong-auth gate — demo login is not strong)
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^comms\/suomifi\/eligibility(?:\?|$)/.test(path(config))) return null;
    return { status: 200, data: { available: false } };
  },
  // active info banners
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^comms\/banners(?:\?|$)/.test(path(config))) return null;
    return { status: 200, data: BANNERS };
  },
  // dismiss banner
  (config) => {
    if (method(config) !== 'POST') return null;
    if (!/^comms\/banners\/[^/?]+\/dismiss(?:\?|$)/.test(path(config)))
      return null;
    return { status: 204, data: undefined };
  },
];

const demoMatchers: Matcher[] = [
  ...messagesMatchers,
  ...poolsMatchers,
  ...ticketsMatchers,
  ...commsMatchers,
  ...reportsDevMatchers,
];

const applyMock = (config: InternalAxiosRequestConfig, hit: MockResult) => {
  config.adapter = async () => ({
    data: hit.data,
    status: hit.status,
    statusText: 'OK (demo)',
    headers: {},
    config,
  });
};

// Strip `rights` from the REAL property detail so every capability-gated action
// (order services, send message, manage pool…) fails open for the demo — see
// src/app/capabilities.ts (rights absent → allow all). Only touches the leaf
// detail response; the address list stays untouched.
const isPropertyDetail = (config: InternalAxiosRequestConfig): boolean => {
  if (config.method?.toUpperCase() !== 'GET') return false;
  return /^property\/[^/?]+\/?(\?|$)/.test(
    (config.url || '').replace(/^\/+/, '')
  );
};

export const installDemoMocks = (...instances: AxiosInstance[]) => {
  instances.forEach((instance) => {
    // Request: serve feature fixtures via a per-request adapter override. The
    // isDemoMode() check re-evaluates per request (cheap, build-constant).
    instance.interceptors.request.use((config) => {
      if (isDemoMode()) {
        const hit = demoMatchers.map((m) => m(config)).find(Boolean);
        if (hit) applyMock(config, hit);
      }
      return config;
    });
    // Response: for the REAL property detail — (1) drop `rights` so every
    // capability-gated action fails open, and (2) capture the property's first
    // portal-enabled ticket-type name so the active demo case survives the
    // inbox's portal-type filter (see the tickets fixtures above).
    instance.interceptors.response.use((response: AxiosResponse) => {
      if (
        isDemoMode() &&
        isPropertyDetail(response.config) &&
        response.data &&
        typeof response.data === 'object'
      ) {
        const data = response.data as {
          rights?: unknown;
          ticket_types?: { name?: unknown }[];
        };
        if ('rights' in data) delete data.rights;
        const firstType = data.ticket_types?.find(
          (t) => typeof t?.name === 'string' && t.name.trim()
        );
        if (firstType && typeof firstType.name === 'string') {
          capturedTicketType = firstType.name;
        }
      }
      return response;
    });
  });
};
